import { Box, IconButton, styled } from '@mui/material';
import Icon from 'designSystem/Primitives/Icon/Icon';
import ThemeTypography from 'designSystem/Primitives/Typography/ThemeTypography';
import React, { FC } from 'react';
import { Booleanish, booleanish } from 'types/booleanish.types';
import { ERiskLevel } from 'types/compliance.types';
import { getCountryLabelFromCode } from 'utils/country.utils';
import RiskBadge from '../RiskBadge/RiskBadge';
import capitalize from 'lodash/capitalize';
import { EGeoDataEntity, GEO_DATA_ENTITY_ICON_NAME } from 'types/geodata.types';

interface IGeoDataItemProps {
  title: string;
  status?: 'valid' | 'error';
  commodity?: string;
  countryCode?: string;
  owner?: string;
  riskLevel?: ERiskLevel;
  active?: boolean;
  checked?: boolean;
  disabled?: boolean;
  geoDataEntity?: EGeoDataEntity;
  /**
   * If only sub set is selected
   */
  totalCount?: number;
  includedCount?: number;
  hasBoxShadow?: boolean;
  fullWidth?: boolean;
  amountFarms?: number;
  amountBatches?: number;
  weight?: number;
  onClickDelete?: () => void;
}

const Container = styled(Box)<{
  'has-error'?: booleanish;
  active?: booleanish;
  disabled?: booleanish;
  'has-box-shadow'?: booleanish;
  'full-width'?: booleanish;
}>(
  ({
    theme,
    active,
    disabled,
    'has-error': hasError,
    'has-box-shadow': hasBoxShadow,
    'full-width': fullWidth,
  }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing(1),
    paddingRight: theme.spacing(2),
    border:
      hasBoxShadow === 'true'
        ? 'none'
        : `1px solid ${
            hasError === 'true'
              ? theme.custom.themeColors.error[100]
              : theme.custom.colors.lightBorderColor
          }`,
    borderRadius: 4,
    background: theme.custom.themeColors.white,
    position: 'relative',
    width: fullWidth === 'true' ? '100%' : 'fit-content',
    height: 48,
    boxShadow: hasBoxShadow === 'true' ? theme.custom.shadows[1] : 'none',

    ':hover': {
      ...(active === 'false' && disabled === 'false'
        ? {
            backgroundColor: theme.custom.themeColors.grayScale[5],
            borderColor: theme.custom.themeColors.primary[40],
          }
        : {}),
    },

    '& .hover-btn': {
      visibility: 'hidden',
    },
    '&:hover .hover-btn': {
      visibility: 'visible',
    },

    ...(active === 'true' && {
      '&:before': {
        content: '""',
        display: 'block',
        width: 8,
        height: '100%',
        position: 'absolute',
        left: 0,
        top: 0,
        borderRadius: '4px 0 0 4px',
        backgroundColor:
          hasError === 'true'
            ? theme.custom.themeColors.error[100]
            : theme.custom.themeColors.primary[100],
        pointerEvents: 'none',
      },
      '&:after': {
        content: '""',
        display: 'block',
        height: '100%',
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        border: `2px solid ${
          hasError === 'true'
            ? theme.custom.themeColors.error[100]
            : theme.custom.themeColors.primary[100]
        }`,
        borderRadius: 4,
        pointerEvents: 'none',
      },
    }),
  })
);

const TextContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const SubTextRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: 'max-content',
}));

const SubText = styled(ThemeTypography)(({ theme }) => ({
  marginLeft: 2,
  fontSize: '10px !important',
}));

const SubItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginRight: theme.spacing(1),
}));

const InfoContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.custom.themeColors.primary[5],
  padding: theme.spacing(0.2, 0.75),
  borderRadius: 4,
}));

const GeoDataItem: FC<IGeoDataItemProps> = ({
  title,
  status,
  commodity,
  countryCode,
  owner,
  riskLevel,
  active,
  checked,
  disabled,
  geoDataEntity,
  totalCount,
  includedCount,
  fullWidth,
  hasBoxShadow,
  amountFarms,
  amountBatches,
  weight,
  onClickDelete,
}) => {
  const handleClickDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClickDelete
      ? onClickDelete()
      : console.warn('DatasetItem: onClickDelete is not defined for this item');
  };

  return (
    <Container
      active={Booleanish(active)}
      disabled={Booleanish(status === 'valid' || disabled)}
      has-error={Booleanish(status === 'error')}
      has-box-shadow={Booleanish(hasBoxShadow)}
      full-width={Booleanish(fullWidth)}
    >
      <Box ml={1} />
      <Icon
        name={geoDataEntity ? GEO_DATA_ENTITY_ICON_NAME[geoDataEntity] : 'polygon-data'}
        size="xx-large"
        color={
          geoDataEntity === EGeoDataEntity.CLUSTER ||
          geoDataEntity === EGeoDataEntity.SITE_SELECTION
            ? 'blue-ice'
            : 'almost-black'
        }
      />
      <TextContainer ml={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box display="flex" alignItems="center" gap={0.75}>
            <ThemeTypography variant="BODY_MEDIUM_BOLD">{title}</ThemeTypography>
            {amountBatches !== undefined && (
              <InfoContainer>
                <ThemeTypography variant="BODY_SMALLER_BOLD" color="BLACK">
                  {amountBatches} batch{amountBatches !== 1 ? 'es' : ''}
                </ThemeTypography>
              </InfoContainer>
            )}
            {weight !== undefined && (
              <InfoContainer>
                <ThemeTypography variant="BODY_SMALLER_BOLD" color="BLACK">
                  {weight} kg
                </ThemeTypography>
              </InfoContainer>
            )}
            {totalCount !== undefined &&
              includedCount !== undefined &&
              includedCount < totalCount && (
                <ThemeTypography variant="BODY_SMALL_BOLD" color="GRAY_60">
                  ({includedCount}/{totalCount})
                </ThemeTypography>
              )}
            {riskLevel && (
              <Box mr={1}>
                <RiskBadge
                  riskLevel={riskLevel}
                  text={capitalize(ERiskLevel[riskLevel]) + ' risk'}
                />
              </Box>
            )}
            {(status === 'valid' || checked) && <Icon name="check-filled" color="green" />}
            {status === 'error' && <Icon name="error-filled" color="red-80" />}
          </Box>

          {!!onClickDelete && !disabled && (
            <IconButton className="hover-btn" size="small" onClick={handleClickDelete}>
              <Icon name="delete" size="small" />
            </IconButton>
          )}
        </Box>
        <SubTextRow>
          {geoDataEntity && (
            <SubItem>
              <Icon name="tag-filled" size="xx-small" />
              <SubText variant="LABEL_INPUT" color="GRAY_80">
                {geoDataEntity === EGeoDataEntity.CLUSTER
                  ? 'Origin cluster'
                  : geoDataEntity === EGeoDataEntity.DATASET
                  ? 'Geo-dataset'
                  : 'Farm'}
              </SubText>
            </SubItem>
          )}
          {amountFarms !== undefined && (
            <SubItem>
              <Icon name="farm" size="small" />
              <SubText variant="LABEL_INPUT" color="GRAY_80">
                {amountFarms} farm{amountFarms !== 1 ? 's' : ''}
              </SubText>
            </SubItem>
          )}
          {commodity && (
            <SubItem>
              <Icon name="raw-material" size="small" />
              <SubText variant="LABEL_INPUT" color="GRAY_80">
                {commodity}
              </SubText>
            </SubItem>
          )}
          {countryCode && (
            <SubItem>
              <Icon name="activity-default" size="small" />
              <SubText variant="LABEL_INPUT" color="GRAY_80">
                {getCountryLabelFromCode(countryCode)}
              </SubText>
            </SubItem>
          )}
          {owner && (
            <SubItem>
              <Icon name="partner" size="small" />
              <SubText variant="LABEL_INPUT" color="GRAY_80">
                {owner}
              </SubText>
            </SubItem>
          )}
        </SubTextRow>
      </TextContainer>
    </Container>
  );
};

export default GeoDataItem;
